import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AnimatedNumber } from '../hooks/useCountUp';
import { 
  User, ShieldCheck, Key, Cube, CheckSquare, UserCircle, 
  Envelope, Buildings, Briefcase, Calendar, Clock, CaretRight,
  SpinnerGap, PaperPlaneTilt, ArrowSquareOut, WarningCircle, Ticket,
  ClipboardText, Lightning, ArrowRight, Pulse, XCircle, CheckCircle,
  HourglassSimple, Lock
} from '@phosphor-icons/react';

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'just now';
}

const STATUS_STYLES = {
  approved: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: HourglassSimple },
  cancelled: { bg: 'bg-slate-100', text: 'text-navy-600', icon: XCircle },
};

const TOP_BAR_COLORS = {
  roles: 'bg-navy-600',
  entitlements: 'bg-navy-600',
  accounts: 'bg-navy-600',
  apps: 'bg-navy-600',
  approvals: 'bg-orange-500',
  certifications: 'bg-orange-500',
  tickets: 'bg-orange-500',
  requests: 'bg-orange-500',
};

function StatCard({ icon: Icon, label, value, topBar, onClick, testId, index }) {
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group animate-fade-in-up"
      style={{ animationDelay: `${(index || 0) * 0.04}s` }}
    >
      <div className={`h-1.5 ${topBar || 'bg-navy-600'}`} />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="shrink-0 transition-transform duration-200 group-hover:scale-110 text-navy-700">
            <Icon size={22} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xl font-bold text-navy-900"><AnimatedNumber value={value} /></p>
            <p className="text-[10px] text-navy-500 font-semibold uppercase tracking-widest">{label}</p>
          </div>
          <CaretRight size={12} className="text-navy-300 group-hover:text-orange-500 transition-colors shrink-0 mt-1" />
        </div>
      </div>
    </div>
  );
}

function QuickLinks({ navigate }) {
  const links = [
    { label: 'Request Access', icon: PaperPlaneTilt, path: '/access-requests', color: 'bg-orange-50 text-orange-700 hover:bg-orange-100' },
    { label: 'View Approvals', icon: CheckSquare, path: '/approvals', color: 'bg-navy-50 text-navy-700 hover:bg-navy-100' },
    { label: 'Privileged Access', icon: Lightning, path: '/jit-access', color: 'bg-orange-50 text-orange-700 hover:bg-orange-100' },
    { label: 'Certifications', icon: ClipboardText, path: '/approvals', color: 'bg-navy-50 text-navy-700 hover:bg-navy-100' },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="quick-links-widget">
      <div className="h-1.5 bg-orange-500" />
      <div className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Lightning size={18} weight="duotone" className="text-orange-500" />
          <h3 className="text-xs font-bold text-navy-900 uppercase tracking-widest">Quick Links</h3>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {links.map(a => (
            <button
              key={a.label}
              data-testid={`quick-link-${a.label.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => navigate(a.path)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-sm text-xs font-semibold transition-colors ${a.color}`}
            >
              <a.icon size={16} weight="duotone" />
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MyIdentityPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadMyIdentity();
  }, []);

  const loadMyIdentity = async () => {
    try {
      const res = await API.get('/my-identity');
      setData(res.data);
    } catch (err) {
      void 0;
      setError('Failed to load your identity data');
    } finally {
      setLoading(false);
    }
  };

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    return `${API.defaults.baseURL}/applications/logo/${logoPath}`;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <SpinnerGap size={32} weight="bold" className="animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-sm p-6 text-center">
          <WarningCircle size={32} weight="duotone" className="text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-700">{error}</p>
          <Button onClick={loadMyIdentity} className="mt-4 bg-red-600 hover:bg-red-700 text-white rounded-sm">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const identity = data?.identity || {};
  const summary = data?.summary || {};
  const roles = data?.roles || [];
  const entitlements = data?.entitlements || [];
  const accounts = data?.accounts || [];
  const applications = data?.applications || [];
  const pendingApprovals = data?.pending_approvals || [];
  const myRequests = data?.my_requests || [];

  const expiringRoles = roles.filter(r => {
    if (!r.expires_at) return false;
    const daysLeft = Math.floor((new Date(r.expires_at) - Date.now()) / 86400000);
    return daysLeft >= 0 && daysLeft <= 30;
  });

  return (
    <div data-testid="my-identity-page" className="p-4 sm:p-6 md:p-8 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>My Identity</h1>
          <p className="text-sm text-navy-500 mt-0.5">Your access profile and permissions</p>
        </div>
        <Button onClick={() => navigate('/access-requests')} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm shadow-sm" data-testid="request-access-button">
          <PaperPlaneTilt size={16} weight="bold" className="mr-1.5" /> Request Access
        </Button>
      </div>

      {/* Identity Card - Compact */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="identity-card">
        <div className="h-1.5 bg-navy-600" />
        <div className="p-5">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-sm bg-navy-100 flex items-center justify-center shrink-0">
              <span className="text-xl font-bold text-navy-600">{identity.display_name?.charAt(0) || '?'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-navy-900 truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {identity.display_name || 'Unknown User'}
                </h2>
                <Badge className={`text-[10px] uppercase ${identity.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-navy-600'}`}>
                  {identity.status || 'active'}
                </Badge>
                {identity.is_user_only && (
                  <Badge className="text-[10px] uppercase bg-amber-100 text-amber-700">No Linked Identity</Badge>
                )}
              </div>
              <div className="flex items-center gap-6 mt-2 text-sm text-navy-500 flex-wrap">
                {identity.email && (
                  <span className="flex items-center gap-1.5">
                    <Envelope size={14} className="text-navy-400" /> {identity.email}
                  </span>
                )}
                {identity.department && (
                  <span className="flex items-center gap-1.5">
                    <Buildings size={14} className="text-navy-400" /> {identity.department}
                  </span>
                )}
                {identity.job_title && (
                  <span className="flex items-center gap-1.5">
                    <Briefcase size={14} className="text-navy-400" /> {identity.job_title}
                  </span>
                )}
                {identity.manager_name && (
                  <span className="flex items-center gap-1.5">
                    <User size={14} className="text-navy-400" /> {identity.manager_name}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stat Cards Grid - matching Dashboard StatCard style */}
      <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 ${summary.can_manage_tickets ? 'xl:grid-cols-7' : 'xl:grid-cols-6'} gap-3`} data-testid="stats-grid">
        <StatCard icon={ShieldCheck} label="Roles" value={summary.total_roles || 0} topBar={TOP_BAR_COLORS.roles}
          onClick={() => document.getElementById('roles-section')?.scrollIntoView({ behavior: 'smooth' })}
          testId="tile-roles" index={0} />
        <StatCard icon={Key} label="Entitlements" value={summary.total_entitlements || 0} topBar={TOP_BAR_COLORS.entitlements}
          onClick={() => document.getElementById('entitlements-section')?.scrollIntoView({ behavior: 'smooth' })}
          testId="tile-entitlements" index={1} />
        <StatCard icon={Cube} label="Applications" value={summary.total_applications || 0} topBar={TOP_BAR_COLORS.apps}
          onClick={() => document.getElementById('apps-section')?.scrollIntoView({ behavior: 'smooth' })}
          testId="tile-apps" index={2} />
        <StatCard icon={CheckSquare} label="Pending Approvals" value={summary.pending_approvals || 0} topBar={TOP_BAR_COLORS.approvals}
          onClick={() => navigate('/approvals')} testId="tile-approvals" index={3} />
        <StatCard icon={ClipboardText} label="Cert Reviews" value={summary.pending_certifications || 0} topBar={TOP_BAR_COLORS.certifications}
          onClick={() => navigate('/approvals')} testId="tile-certifications" index={4} />
        <StatCard icon={PaperPlaneTilt} label="My Requests" value={summary.pending_requests || 0} topBar={TOP_BAR_COLORS.requests}
          onClick={() => navigate('/access-requests')} testId="tile-requests" index={5} />
        {summary.can_manage_tickets && (
          <StatCard icon={Ticket} label="Tickets" value={summary.pending_tickets || 0} topBar={TOP_BAR_COLORS.tickets}
            onClick={() => navigate('/provisioning-tickets')} testId="tile-tickets" index={6} />
        )}
      </div>

      {/* Middle Grid: Quick Links + Roles / Pending Items + Entitlements */}
      <div className="grid lg:grid-cols-2 xl:grid-cols-12 gap-4">
        {/* Left Column */}
        <div className="xl:col-span-4 space-y-4">
          {/* Quick Links */}
          <QuickLinks navigate={navigate} />

          {/* Expiring Access Warning */}
          {expiringRoles.length > 0 && (
            <div className="bg-white border border-amber-200 rounded-sm shadow-sm overflow-hidden" data-testid="expiring-access-widget">
              <div className="h-1.5 bg-orange-500" />
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar size={16} weight="duotone" className="text-amber-500" />
                  <h3 className="text-xs font-bold text-navy-900 uppercase tracking-widest">Expiring Soon</h3>
                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">{expiringRoles.length}</Badge>
                </div>
                <div className="space-y-2">
                  {expiringRoles.slice(0, 3).map((role, idx) => {
                    const daysLeft = Math.floor((new Date(role.expires_at) - Date.now()) / 86400000);
                    return (
                      <div key={role.id || idx} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <ShieldCheck size={12} className="text-amber-500" />
                          <span className="text-navy-700 font-medium">{role.name}</span>
                        </div>
                        <span className={`font-semibold ${daysLeft <= 7 ? 'text-red-600' : 'text-amber-600'}`}>
                          {daysLeft}d left
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* My Accounts */}
          <div id="accounts-section" className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
            <div className="h-1.5 bg-navy-600" />
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <User size={16} weight="duotone" className="text-navy-600" />
              <h3 className="text-xs font-bold text-navy-900 uppercase tracking-widest">My Accounts ({accounts.length})</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {accounts.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-navy-400">
                  <User size={24} weight="duotone" className="mx-auto mb-1 text-navy-300" />
                  No linked accounts
                </div>
              ) : accounts.map((account, idx) => (
                <div key={account.id || idx} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                  <User size={14} weight="duotone" className="text-navy-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy-900 truncate">{account.username}</p>
                    <p className="text-[10px] text-navy-400">{account.type}</p>
                  </div>
                  <Badge className={`text-[9px] ${account.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-navy-600'}`}>
                    {account.status}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Middle Column - Roles + Apps */}
        <div className="xl:col-span-4 space-y-4">
          {/* My Roles */}
          <div id="roles-section" className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
            <div className="h-1.5 bg-navy-600" />
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} weight="duotone" className="text-navy-600" />
                <h3 className="text-xs font-bold text-navy-900 uppercase tracking-widest">My Roles ({roles.length})</h3>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {roles.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <ShieldCheck size={28} weight="duotone" className="mx-auto mb-2 text-navy-300" />
                  <p className="text-sm text-navy-400">No roles assigned</p>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/access-requests')} className="text-orange-600 mt-2 text-xs">
                    Request Access <ArrowRight size={12} className="ml-1" />
                  </Button>
                </div>
              ) : roles.map((role, idx) => (
                <div key={role.id || `role-${idx}`} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors" data-testid={`my-role-${idx}`}>
                  <ShieldCheck size={14} weight="duotone" className="text-navy-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy-900 truncate">{role.name}</p>
                    <p className="text-[10px] text-navy-400 truncate">{role.description || 'No description'}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge className="text-[9px] bg-amber-100 text-amber-700">
                      <Key size={8} className="mr-0.5" />{role.entitlement_count}
                    </Badge>
                    {role.expires_at && (
                      <Badge className="text-[9px] bg-slate-100 text-navy-600">
                        <Calendar size={8} className="mr-0.5" />{new Date(role.expires_at).toLocaleDateString()}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* My Entitlements */}
          <div id="entitlements-section" className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
            <div className="h-1.5 bg-navy-600" />
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <Key size={16} weight="duotone" className="text-navy-600" />
              <h3 className="text-xs font-bold text-navy-900 uppercase tracking-widest">My Entitlements ({entitlements.length})</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {entitlements.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-navy-400">
                  <Key size={24} weight="duotone" className="mx-auto mb-1 text-navy-300" />
                  No entitlements granted
                </div>
              ) : entitlements.slice(0, 8).map((ent) => (
                <div key={ent.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                  <Key size={12} weight="duotone" className="text-navy-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-navy-900 truncate">{ent.name}</p>
                  </div>
                  <Badge className={`text-[9px] ${ent.source === 'direct_azure' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-navy-600'}`}>
                    {ent.source === 'direct_azure' ? 'Azure AD' : 'Via Role'}
                  </Badge>
                </div>
              ))}
              {entitlements.length > 8 && (
                <div className="px-4 py-2 text-center text-[10px] text-navy-400">
                  +{entitlements.length - 8} more entitlements
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Pending Items + Recent Requests */}
        <div className="xl:col-span-4 space-y-4">
          {/* Pending Approvals */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="pending-approvals-widget">
            <div className="h-1.5 bg-orange-500" />
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckSquare size={16} weight="duotone" className="text-orange-500" />
                <h3 className="text-xs font-bold text-navy-900 uppercase tracking-widest">Pending Approvals</h3>
                {pendingApprovals.length > 0 && (
                  <Badge className="bg-orange-100 text-orange-700 text-[10px]">{pendingApprovals.length}</Badge>
                )}
              </div>
              {pendingApprovals.length > 0 && (
                <Button variant="ghost" size="sm" className="text-xs text-navy-500 h-7" onClick={() => navigate('/approvals')}>
                  View All <ArrowRight size={12} className="ml-1" />
                </Button>
              )}
            </div>
            <div className="divide-y divide-slate-100">
              {pendingApprovals.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-navy-400">
                  <CheckCircle size={24} weight="duotone" className="mx-auto mb-1 text-emerald-400" />
                  No pending approvals
                </div>
              ) : pendingApprovals.slice(0, 4).map((approval) => (
                <div key={approval.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate('/approvals')}>
                  <div className="w-8 h-8 rounded-sm bg-orange-100 flex items-center justify-center shrink-0">
                    {approval.type === 'jit_access' ? (
                      <Lightning size={14} weight="duotone" className="text-orange-600" />
                    ) : (
                      <CheckSquare size={14} weight="duotone" className="text-orange-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-navy-700 truncate">{approval.title}</p>
                    <p className="text-[10px] text-navy-400">{timeAgo(approval.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* My Recent Requests */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="recent-requests-widget">
            <div className="h-1.5 bg-orange-500" />
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Pulse size={16} weight="duotone" className="text-orange-500" />
                <h3 className="text-xs font-bold text-navy-900 uppercase tracking-widest">My Recent Requests</h3>
              </div>
              {myRequests.length > 0 && (
                <Button variant="ghost" size="sm" className="text-xs text-navy-500 h-7" onClick={() => navigate('/access-requests')}>
                  View All <ArrowRight size={12} className="ml-1" />
                </Button>
              )}
            </div>
            <div className="divide-y divide-slate-100">
              {myRequests.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-navy-400">
                  <PaperPlaneTilt size={24} weight="duotone" className="mx-auto mb-1 text-navy-300" />
                  No access requests yet
                </div>
              ) : myRequests.slice(0, 5).map((req) => {
                const st = STATUS_STYLES[req.status] || STATUS_STYLES.pending;
                const StatusIcon = st.icon;
                return (
                  <div key={req.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                    <div className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ${st.bg}`}>
                      <StatusIcon size={14} weight="bold" className={st.text} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-navy-700 truncate">
                        {req.type === 'role' ? <ShieldCheck size={12} weight="duotone" className="inline mr-1 text-navy-400" /> : null}
                        {req.target_name}
                      </p>
                      <p className="text-[10px] text-navy-400">
                        {req.type} request &middot; {timeAgo(req.created_at)}
                        {req.current_approval_step > 0 && req.status === 'pending' && (
                          <span className="ml-1 text-amber-600">Step {req.current_approval_step}/{req.approval_workflow?.length || '?'}</span>
                        )}
                      </p>
                    </div>
                    <Badge className={`text-[9px] uppercase ${st.bg} ${st.text}`}>
                      {req.status}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Applications Section - Bottom Full Width */}
      {applications.length > 0 && (
        <div id="apps-section" className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
          <div className="h-1.5 bg-navy-600" />
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <Cube size={16} weight="duotone" className="text-navy-600" />
            <h3 className="text-xs font-bold text-navy-900 uppercase tracking-widest">My Applications ({applications.length})</h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {applications.map((app) => (
                <div key={app.id} className="border border-slate-200 rounded-sm p-3 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group cursor-pointer">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-sm bg-slate-100 flex items-center justify-center shrink-0">
                      {app.logo_path ? (
                        <img src={getLogoUrl(app.logo_path)} alt={app.name} className="w-full h-full object-cover rounded-sm"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <Cube size={18} weight="duotone" className="text-navy-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy-900 truncate">{app.name}</p>
                      <p className="text-[10px] text-navy-400 truncate">{app.description || 'No description'}</p>
                      <Badge className="text-[9px] mt-1.5 bg-navy-100 text-navy-600">
                        <ShieldCheck size={8} className="mr-0.5" />{app.role_count} roles
                      </Badge>
                    </div>
                    <CaretRight size={12} className="text-navy-300 group-hover:text-orange-500 transition-colors shrink-0 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
